import { createContext, useContext, useState, type ReactNode } from 'react';

export type GlobalTimeRange = 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export const TIME_RANGE_LABELS: Record<GlobalTimeRange, string> = {
  today: '今日',
  yesterday: '昨日',
  week: '本周',
  month: '本月',
  quarter: '本季度',
  year: '本年',
  custom: '自定义',
};

interface GlobalFilterContextType {
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  timeRange: GlobalTimeRange;
  setTimeRange: (v: GlobalTimeRange) => void;
  customStartDate: string;
  setCustomStartDate: (v: string) => void;
  customEndDate: string;
  setCustomEndDate: (v: string) => void;
}

const GlobalFilterContext = createContext<GlobalFilterContextType>({
  searchQuery: '',
  setSearchQuery: () => {},
  timeRange: 'today',
  setTimeRange: () => {},
  customStartDate: '',
  setCustomStartDate: () => {},
  customEndDate: '',
  setCustomEndDate: () => {},
});

export function GlobalFilterProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [timeRange, setTimeRange] = useState<GlobalTimeRange>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  return (
    <GlobalFilterContext.Provider value={{
      searchQuery, setSearchQuery,
      timeRange, setTimeRange,
      customStartDate, setCustomStartDate,
      customEndDate, setCustomEndDate,
    }}>
      {children}
    </GlobalFilterContext.Provider>
  );
}

export function useGlobalFilter() {
  return useContext(GlobalFilterContext);
}

/** 获取本地时区的日期字符串 (YYYY-MM-DD)，避免 toISOString 的 UTC 偏移问题 */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 判断日期是否在全局时间范围内 */
export function isInGlobalTimeRange(
  date: string,
  range: GlobalTimeRange,
  customStartDate?: string,
  customEndDate?: string,
): boolean {
  if (!date) return false;
  const d = new Date(date + 'T00:00:00');
  const now = new Date();
  const todayStr = toLocalDateStr(now);
  const dateStr = toLocalDateStr(d);

  switch (range) {
    case 'today':
      return dateStr === todayStr;
    case 'yesterday': {
      const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      return dateStr === toLocalDateStr(y);
    }
    case 'week': {
      // 本周：从本周一开始到今天（自然周，非滚动7天）
      const dayOfWeek = now.getDay(); // 0=周日, 1=周一
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - mondayOffset);
      const mondayStr = toLocalDateStr(monday);
      return dateStr >= mondayStr && dateStr <= todayStr;
    }
    case 'month':
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return Math.floor(d.getMonth() / 3) === q && d.getFullYear() === now.getFullYear();
    }
    case 'year':
      return d.getFullYear() === now.getFullYear() && dateStr <= todayStr;
    case 'custom': {
      if (!customStartDate && !customEndDate) return true;
      if (customStartDate && dateStr < customStartDate) return false;
      if (customEndDate && dateStr > customEndDate) return false;
      return true;
    }
    default:
      return true;
  }
}
